import React, { useState } from 'react';
import { Button, Upload, Table, message } from 'antd';
import { InboxOutlined, MailOutlined, DownloadOutlined } from '@ant-design/icons';
import * as XLSX from 'xlsx';
import api from '../api/axios';

export default function ReminderMails() {

    const [data, setData] = useState([]);
    const [sending, setSending] = useState(false);
    const [sentCount, setSentCount] = useState(0);
    const [customSubject, setCustomSubject] = useState('');
    const [customBody, setCustomBody] = useState('');

    const columns = [
        { title: 'Service Sheet #', dataIndex: 'Service_Sheet_', key: 'Service_Sheet_' },
        { title: 'Customer', dataIndex: 'Customer', key: 'Customer' },
        { title: 'Related PO', dataIndex: 'Related_PO', key: 'Related_PO' },
        { title: 'Date', dataIndex: 'displayDate', key: 'displayDate' },
        { title: 'Amount', dataIndex: 'Amount', key: 'Amount' },
        { title: 'Currency', dataIndex: 'Currency', key: 'Currency' },
        { title: 'Routing Status', dataIndex: 'Routing_Status', key: 'Routing_Status' },
        { title: 'Status', dataIndex: 'Status', key: 'Status' },
        { title: 'SES Approver', dataIndex: 'SES Approver', key: 'SES Approver' },
        { title: 'L1', dataIndex: 'L1', key: 'L1' },
        { title: 'L2', dataIndex: 'L2', key: 'L2' },
    ];

    // Excel serial date -> JS Date
    const excelSerialToDate = (serial) => {
        if (!serial && serial !== 0) return null;
        const n = Number(serial);
        if (Number.isNaN(n)) return new Date(serial);
        return new Date((n - 25569) * 86400 * 1000);
    };

    // Exact same filter as backend
    const isEligibleForReminder = (row) => {
        const status = String(row.Status || '').trim().toLowerCase();
        const approver = String(row['SES Approver'] || '').trim();

        if (!approver || status !== 'sent') return false;

        let docDate;
        const dateRaw = row.Date || row._rawDate;

        if (typeof dateRaw === 'number') {
            docDate = new Date((Number(dateRaw) - 25569) * 86400 * 1000);
        } else if (dateRaw) {
            const parts = String(dateRaw).split('/');
            if (parts.length === 3) {
                const month = parseInt(parts[0], 10) - 1;
                const day = parseInt(parts[1], 10);
                const year = parseInt(parts[2], 10);
                docDate = new Date(year, month, day);
            } else {
                docDate = new Date(dateRaw);
            }
        }

        if (isNaN(docDate?.getTime())) return false;

        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const rowDate = new Date(docDate);
        rowDate.setHours(0, 0, 0, 0);

        return rowDate < today;
    };

    // NEW: Download sample template
    const handleDownloadSample = async () => {
        try {
            const response = await api.get('/remindermails/sample', {
                responseType: 'blob'
            });

            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'reminder_template.xlsx');
            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);
            window.URL.revokeObjectURL(url);

            message.success('Sample template downloaded!');
        } catch (error) {
            console.error('Download error:', error);
            message.error('Failed to download sample');
        }
    };

    const handleFileUpload = ({ file }) => {
        const reader = new FileReader();
        reader.onload = (e) => {
            const data = new Uint8Array(e.target.result);
            const workbook = XLSX.read(data, { type: 'array' });

            // Read data sheet (first sheet)
            const dataSheetName = workbook.SheetNames[0];
            const dataWorksheet = workbook.Sheets[dataSheetName];
            const jsonData = XLSX.utils.sheet_to_json(dataWorksheet);

            const mapped = jsonData.map(row => {
                const jsDate = excelSerialToDate(row.Date);
                return {
                    ...row,
                    _rawDate: row.Date,
                    displayDate: jsDate ? jsDate.toLocaleDateString('en-GB') : row.Date
                };
            });

            setData(mapped);

            // NEW: Read Mail_Template sheet for custom template
            const mailSheet = workbook.Sheets['Mail_Template'];
            if (mailSheet) {
                const mailData = XLSX.utils.sheet_to_json(mailSheet);
                console.log('📧 Mail template found:', mailData);

                if (mailData.length > 0) {
                    const subject = mailData[0].Subject || '';
                    const body = mailData[0].Body || '';
                    console.log('📧 Extracted:', { subject: subject.substring(0, 50), bodyLength: body.length });

                    // Store in state (add these states to component)
                    setCustomSubject(subject);
                    setCustomBody(body);
                    message.success(`${mapped.length} rows + 📧 Custom template loaded!`);
                }
            } else {
                message.success(`${mapped.length} rows loaded`);
            }

            setSentCount(0);
        };
        reader.readAsArrayBuffer(file);
        return false;
    };


    const handleSendReminders = async () => {
        const pending = data.filter(row => isEligibleForReminder(row));

        console.log('🚀 Sending:', {
            pendingCount: pending.length,
            customSubject: customSubject.substring(0, 50),
            customBodyLength: customBody.length
        });

        if (pending.length === 0) {
            message.info('No pending reminders found');
            return;
        }

        setSending(true);
        try {
            const response = await api.post('/remindermails', {
                reminders: pending,
                customSubject,
                customBody
            });

            message.success(`${pending.length} reminders sent!`);
            setSentCount(pending.length);
        } catch (error) {
            message.error('Failed to send reminders');
        }
        setSending(false);
    };


    const pendingCount = data.filter(row => isEligibleForReminder(row)).length;
    const showSentStatus = sentCount > 0;
    const statusLabel = showSentStatus
        ? `${sentCount}/${data.length} mails sent`
        : `Pending: ${pendingCount} / ${data.length}`;

    return (
        <div style={{ padding: 24 }}>
            <h2>📧 Mail Reminders</h2>

            {/* NEW: Download Sample Button */}
            <div style={{ marginBottom: 24 }}>
                <div style={{
                    display: 'flex',
                    gap: 16,
                    alignItems: 'center',
                    flexWrap: 'wrap'
                }}>
                    {/* Download Button */}
                    <Button
                        icon={<DownloadOutlined />}
                        onClick={handleDownloadSample}
                        type="dashed"
                        size="large"
                    >
                        📥 Download Sample
                    </Button>

                    {/* Upload Button */}
                    <Upload
                        accept=".xlsx,.xls"
                        customRequest={handleFileUpload}
                        showUploadList={false}
                    >
                        <Button icon={<InboxOutlined />} size="large">
                            📁 Upload Excel File
                        </Button>
                    </Upload>
                </div>
            </div>

            <div style={{ margin: '24px 0', padding: '16px', background: '#f5f5f5', borderRadius: 8 }}>
                <Button
                    type="primary"
                    icon={<MailOutlined />}
                    onClick={handleSendReminders}
                    loading={sending}
                    disabled={data.length === 0 || pendingCount === 0 || showSentStatus}
                    size="large"
                >
                    {showSentStatus ? '✅ Mails Sent' : `📧 Send ${pendingCount} Reminders`}
                </Button>
                <span style={{
                    marginLeft: 24,
                    fontSize: 16,
                    color: showSentStatus ? '#52c41a' : '#666',
                    fontWeight: showSentStatus ? 'bold' : '500'
                }}>
        {statusLabel}
    </span>
            </div>

            {data.length > 0 && (
                <Table
                    dataSource={data}
                    columns={columns}
                    pagination={{ pageSize: 20 }}
                    scroll={{ x: 1400 }}
                    rowKey="Service_Sheet_"
                    size="small"
                />
            )}

        </div>
    );
};

